import { useEffect, useRef } from 'react';

/**
 * Custom React hook for Mobile WebOTP API SMS Auto-Detection & Native Paste.
 * Clean, non-intrusive implementation:
 * - Listens for native incoming carrier SMS via WebOTP API (navigator.credentials.get).
 * - Handles explicit user paste events (Ctrl+V / Long-press Paste).
 * - NO silent background clipboard polling or automatic readText calls that interfere with manual typing.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Active when the OTP input step/modal is visible
 * @param {Function} options.onOtpReceived - Callback invoked with the parsed 6-digit code string
 */
export const useWebOTP = ({ enabled = false, onOtpReceived }) => {
  const onOtpReceivedRef = useRef(onOtpReceived);
  const lastProcessedOtpRef = useRef('');

  useEffect(() => {
    onOtpReceivedRef.current = onOtpReceived;
  }, [onOtpReceived]);

  // Reset last processed OTP when enabled changes or resets
  useEffect(() => {
    if (!enabled) {
      lastProcessedOtpRef.current = '';
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const processCode = (code, source) => {
      const cleanDigits = String(code || '').replace(/\D/g, '').slice(0, 6);
      if (cleanDigits.length === 6 && cleanDigits !== lastProcessedOtpRef.current) {
        lastProcessedOtpRef.current = cleanDigits;
        console.log(`[OTP AutoFill] 6-Digit Code detected via ${source}:`, cleanDigits);
        if (typeof onOtpReceivedRef.current === 'function') {
          onOtpReceivedRef.current(cleanDigits);
        }
      }
    };

    // 1. Global Native Paste Event Handler (Fires ONLY when user explicitly performs a Paste action)
    const handleGlobalPaste = (e) => {
      try {
        const pastedData = e.clipboardData ? e.clipboardData.getData('text') : '';
        if (pastedData) {
          const cleanDigits = pastedData.replace(/\D/g, '');
          if (cleanDigits.length >= 6) {
            processCode(cleanDigits, 'Global Paste');
          }
        }
      } catch (err) {
        // Ignore
      }
    };

    window.addEventListener('paste', handleGlobalPaste);

    // 2. Browser Native WebOTP API (Fires ONLY when genuine incoming SMS OTP arrives from carrier)
    let abortController = null;
    if ('OTPCredential' in window && navigator.credentials) {
      console.log('[WebOTP] Listening for incoming SMS OTP...');
      abortController = new AbortController();

      navigator.credentials
        .get({
          otp: { transport: ['sms'] },
          signal: abortController.signal,
        })
        .then((otpCredential) => {
          if (otpCredential && otpCredential.code) {
            processCode(otpCredential.code, 'WebOTP API');
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            console.warn('[WebOTP] SMS detection notice:', err);
          }
        });
    }

    return () => {
      window.removeEventListener('paste', handleGlobalPaste);
      if (abortController) {
        abortController.abort();
      }
    };
  }, [enabled]);
};

export default useWebOTP;
